import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Flame, Mail, Phone, Zap, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * PromoPage - 29-second cinematic demo for Instagram Reels / Sizzle page
 * 
 * Script timing:
 * [0:00–0:02] Ever forget what you were just doing?
 * [0:02–0:06] You open your phone… and it's gone.
 * [0:06–0:10] So you just drop it in. (typing a task)
 * [0:10–0:14] Move it when you start. (drag to Doing)
 * [0:14–0:18] Calls, emails… it sorts itself out. (auto categories)
 * [0:18–0:21] Mark what matters. (highlight important)
 * [0:21–0:24] Finish it… it's done. (tap check → ripple + flame)
 * [0:24–0:27] And if you lose your place— (tap Find My Thread)
 * [0:27–0:29] you're right back. + tagline
 */

const PromoPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);
  const [hasEnded, setHasEnded] = useState(false);
  const timerRef = useRef(null);

  // Scene definitions with timing (in ms from start) - ~26 seconds total
  const scenes = [
    { id: 0, start: 0, text: "Ever forget what you were just doing?", showApp: false },
    { id: 1, start: 2000, text: "You open your phone… and it's gone.", showApp: false },
    { id: 2, start: 5000, text: "So you just drop it in.", showApp: true, action: "typing" },
    { id: 3, start: 9000, text: "Move it when you start.", showApp: true, action: "dragging" },
    { id: 4, start: 12000, text: "Calls, emails… it sorts itself out.", showApp: true, action: "categories" },
    { id: 5, start: 15000, text: "Mark what matters.", showApp: true, action: "important" },
    { id: 6, start: 18000, text: "Finish it… it's done.", showApp: true, action: "complete" },
    { id: 7, start: 21000, text: "And if you lose your place—", showApp: true, action: "findThread" },
    { id: 8, start: 24000, text: "you're right back.", showApp: true, action: "snapBack" },
    { id: 9, start: 26000, text: "", showApp: false, action: "end" },
  ];

  const startDemo = () => {
    setIsPlaying(true);
    setHasEnded(false);
    setCurrentScene(0);

    // Schedule scene changes
    scenes.forEach((scene, index) => {
      setTimeout(() => {
        if (index < scenes.length - 1) {
          setCurrentScene(index);
        } else {
          setIsPlaying(false);
          setHasEnded(true);
        }
      }, scene.start);
    });
  };

  const resetDemo = () => {
    setIsPlaying(false);
    setHasEnded(false);
    setCurrentScene(0);
  };

  const currentSceneData = scenes[currentScene] || scenes[0];

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col items-center justify-center p-4">
      {/* Mobile-optimized container (9:16 aspect ratio for IG Reels) */}
      <div 
        className="relative w-full max-w-[390px] aspect-[9/16] bg-gradient-to-b from-[#FDFBF7] to-[#F5F3EF] rounded-3xl overflow-hidden shadow-2xl"
        style={{ maxHeight: '85vh' }}
      >
        {/* Demo content */}
        <AnimatePresence mode="wait">
          {!isPlaying && !hasEnded ? (
            // Start screen
            <motion.div
              key="start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center p-8"
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-[#27272A] mb-2">Where was i</h1>
                <p className="text-sm text-stone-500">26-second demo</p>
              </div>
              
              <motion.button
                onClick={startDemo}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#0CBCBF] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg shadow-[#0CBCBF]/30"
              >
                Play Demo
              </motion.button>
            </motion.div>
          ) : hasEnded ? (
            // End screen
            <motion.div
              key="end"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-gradient-to-b from-[#FDFBF7] to-[#F0EEEA]"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <h2 className="text-xl font-semibold text-[#27272A] mb-2">
                  Quiet clarity.
                </h2>
                <p className="text-lg text-stone-500 mb-8">
                  Without the weight.
                </p>
                
                <div className="space-y-3">
                  <Link
                    to="/app"
                    className="block bg-[#F58227] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg shadow-[#F58227]/30 hover:bg-[#D96D19] transition-colors"
                  >
                    Start Free
                  </Link>
                  
                  <button
                    onClick={resetDemo}
                    className="flex items-center justify-center gap-2 mx-auto text-stone-500 hover:text-stone-700 transition-colors"
                  >
                    <RotateCcw size={16} />
                    <span className="text-sm">Watch again</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            // Active demo
            <motion.div
              key="demo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col"
            >
              {/* Caption overlay */}
              <div className="absolute top-0 left-0 right-0 z-20 p-6 pt-10">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={currentScene}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-center text-lg font-medium text-[#27272A] leading-relaxed"
                  >
                    {currentSceneData.text}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* App mockup area */}
              {currentSceneData.showApp && (
                <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-8">
                  <PromoAppMockup action={currentSceneData.action} />
                </div>
              )}

              {/* Blank screen for intro scenes */}
              {!currentSceneData.showApp && (
                <div className="flex-1 flex items-center justify-center">
                  <motion.div
                    animate={{ opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-16 h-16 rounded-full bg-stone-200"
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Instructions below */}
      <p className="text-stone-500 text-sm mt-6 text-center">
        Screen record this for Instagram Reels
      </p>
    </div>
  );
};

// Animated app mockup component
const PromoAppMockup = ({ action }) => {
  const [typedText, setTypedText] = useState("");
  const [taskPosition, setTaskPosition] = useState("todo");
  const [showCategories, setShowCategories] = useState(false);
  const [isImportant, setIsImportant] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showRipple, setShowRipple] = useState(false);
  const [flameGlow, setFlameGlow] = useState(false);
  const [showFindThread, setShowFindThread] = useState(false);
  const [showSnapBack, setShowSnapBack] = useState(false);
  const [completedCount, setCompletedCount] = useState(7);

  // Handle different actions
  useEffect(() => {
    if (action === "typing") {
      const text = "Finish the proposal";
      let i = 0;
      const interval = setInterval(() => {
        setTypedText(text.slice(0, i + 1));
        i++;
        if (i >= text.length) clearInterval(interval);
      }, 100);
      return () => clearInterval(interval);
    }
    
    if (action === "dragging") {
      setTimeout(() => setTaskPosition("doing"), 500);
    }
    
    if (action === "categories") {
      setTimeout(() => setShowCategories(true), 300);
    }
    
    if (action === "important") {
      setTimeout(() => setIsImportant(true), 300);
    }
    
    if (action === "complete") {
      setTimeout(() => {
        setIsComplete(true);
        setShowRipple(true);
        setFlameGlow(true);
        setCompletedCount(8);
      }, 500);
      setTimeout(() => setShowRipple(false), 1500);
      setTimeout(() => setFlameGlow(false), 2000);
    }
    
    if (action === "findThread") {
      setTimeout(() => setShowFindThread(true), 300);
    }
    
    if (action === "snapBack") {
      setShowFindThread(false);
      setTimeout(() => setShowSnapBack(true), 200);
    }
  }, [action]);

  return (
    <div className="w-full max-w-[320px] bg-white rounded-2xl shadow-xl border border-stone-200/60 overflow-hidden">
      {/* App header */}
      <div className="bg-gradient-to-r from-stone-50 to-stone-100/50 px-4 py-3 border-b border-stone-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-[#F58227]" />
          <span className="text-sm font-semibold text-stone-700">Where was i</span>
        </div>
        <div className="flex items-center gap-1.5">
          <motion.div
            animate={flameGlow ? { scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.5 }}
          >
            <Flame 
              size={16} 
              className={flameGlow ? "text-orange-500 fill-orange-400" : "text-orange-400"} 
            />
          </motion.div>
          <span className="text-xs font-semibold text-orange-500">{completedCount}/10</span>
        </div>
      </div>

      {/* App content */}
      <div className="p-3 min-h-[280px]">
        {/* Typing scene */}
        {action === "typing" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#FDFBF7] rounded-xl p-3 border border-stone-200"
          >
            <p className="text-sm text-stone-700">
              {typedText}
              <motion.span
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block w-0.5 h-4 bg-[#F58227] ml-0.5"
              />
            </p>
          </motion.div>
        )}

        {/* Dragging scene */}
        {action === "dragging" && (
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">To Do</p>
              <AnimatePresence>
                {taskPosition === "todo" && (
                  <motion.div
                    exit={{ opacity: 0, x: 100 }}
                    className="bg-white rounded-xl p-3 border border-stone-200 shadow-sm"
                  >
                    <p className="text-sm text-stone-700">Finish the proposal</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Doing</p>
              <AnimatePresence>
                {taskPosition === "doing" && (
                  <motion.div
                    initial={{ opacity: 0, x: -100 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-white rounded-xl p-3 border-2 border-[#F58227] shadow-md"
                  >
                    <p className="text-sm text-stone-700">Finish the proposal</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Categories scene */}
        {action === "categories" && (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">To Do</p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-3"
            >
              <p className="text-[9px] font-semibold text-stone-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Mail size={10} /> Emails
              </p>
              <div className="bg-white rounded-xl p-2.5 border border-stone-200 shadow-sm">
                <p className="text-xs text-stone-700">Email client about invoice</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-[9px] font-semibold text-stone-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Phone size={10} /> Calls
              </p>
              <div className="bg-white rounded-xl p-2.5 border border-stone-200 shadow-sm">
                <p className="text-xs text-stone-700">Call mom about dinner</p>
              </div>
            </motion.div>
          </div>
        )}

        {/* Important scene */}
        {action === "important" && (
          <div>
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Doing</p>
            <motion.div
              animate={isImportant ? { backgroundColor: "#FEF9E7" } : {}}
              className="bg-white rounded-xl p-3 border border-stone-200 shadow-sm relative overflow-hidden"
            >
              {isImportant && (
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 0.5 }}
                  className="absolute left-0 top-0 bottom-0 bg-amber-200/50"
                  style={{ clipPath: "polygon(0 0, 100% 0, 95% 100%, 0% 100%)" }}
                />
              )}
              <p className="text-sm text-stone-700 relative z-10">Finish the proposal</p>
              {isImportant && (
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute top-2 right-2 text-amber-500 font-bold"
                >
                  *
                </motion.span>
              )}
            </motion.div>
          </div>
        )}

        {/* Complete scene */}
        {action === "complete" && (
          <div className="relative">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Doing</p>
            <motion.div
              animate={isComplete ? { opacity: 0.6, scale: 0.98 } : {}}
              className="bg-white rounded-xl p-3 border border-stone-200 shadow-sm relative overflow-hidden"
            >
              <div className="flex items-center justify-between">
                <p className={`text-sm ${isComplete ? "line-through text-stone-400" : "text-stone-700"}`}>
                  Finish the proposal
                </p>
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isComplete ? "bg-green-500" : "border-2 border-stone-300"
                  }`}
                >
                  {isComplete && <Check size={14} className="text-white" />}
                </motion.div>
              </div>
              
              {/* Ripple effect */}
              {showRipple && (
                <motion.div
                  initial={{ scale: 0, opacity: 0.6 }}
                  animate={{ scale: 4, opacity: 0 }}
                  transition={{ duration: 1 }}
                  className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-green-400"
                />
              )}
            </motion.div>
          </div>
        )}

        {/* Find Thread scene */}
        {(action === "findThread" || action === "snapBack") && (
          <div className="flex flex-col items-center justify-center h-full py-8">
            {showFindThread && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#E8EDEB] text-stone-600 px-6 py-3 rounded-full font-medium text-sm flex items-center gap-2"
                >
                  <RotateCcw size={16} />
                  Find my thread
                </motion.button>
              </motion.div>
            )}
            
            {showSnapBack && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#E0F7FA] rounded-xl p-4 text-center"
              >
                <p className="text-xs text-stone-500 mb-1">You were on:</p>
                <p className="text-sm font-medium text-[#00838F]">Finish the proposal</p>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoPage;
